import { RefObject } from "react";
import { colorMap } from "../helpers/Colors";

export const addToRefsGeneric = <T extends HTMLElement>(
  refArray: RefObject<T[]>
) => {
  return (el: T | null) => {
    if (el && !refArray.current.includes(el)) {
      refArray.current.push(el);
    }
  };
};

export const cleanColorName = (raw: string) => raw.split("(")[0].trim();

export const renderCanvas = (
  index: number,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  imagesRef: React.RefObject<HTMLImageElement[]>,
  scaleRef: React.RefObject<{
    value: number;
  }>
) => {
  const canvas = canvasRef?.current;
  if (!canvas) return;
  const context = canvas.getContext("2d");
  if (!context) return;
  const img = imagesRef?.current[index];

  if (!img) return;
  const scale = scaleRef?.current.value || 1;
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;
  const scaledWidth = canvasWidth * scale;
  const scaledHeight = canvasHeight * scale;
  const dx = Math.max((canvasWidth - scaledWidth) / 2, 0);
  const dy = Math.max((canvasHeight - scaledHeight) / 2, 0);
  context.clearRect(0, 0, canvasWidth, canvasHeight);
  context.drawImage(img, dx, dy, scaledWidth, scaledHeight);
};

export const isUrl = (v: string) => /^https?:\/\//i.test(v);
export const toColor = (name: string) => colorMap[name] ?? name ?? "#000";

// Construye capas cuando hay imagen + colores
export function buildLayeredBackground(stops: string[]): {
  backgroundImage?: string;
  backgroundColor?: string;
} {
  // Normaliza: convierte nombres a colores hex si existen en colorMap
  const norm = stops.map((s) => (isUrl(s) ? s : toColor(s)));

  // Casos: 1, 2 o 3 segmentos
  if (norm.length === 1) {
    const a = norm[0];
    return isUrl(a) ? { backgroundImage: `url(${a})` } : { backgroundColor: a };
  }

  if (norm.length === 2) {
    const [a, b] = norm;
    // Imagen + color (en cualquier orden)
    if (isUrl(a) && !isUrl(b)) {
      // Izquierda = imagen, derecha = color
      return {
        backgroundImage: `linear-gradient(to right, rgba(0,0,0,0) 0 50%, ${b} 50% 100%), url(${a})`,
      };
    }
    if (!isUrl(a) && isUrl(b)) {
      // Izquierda = color, derecha = imagen
      return {
        backgroundImage: `linear-gradient(to right, ${a} 0 50%, rgba(0,0,0,0) 50% 100%), url(${b})`,
      };
    }
    // Color + color
    return {
      backgroundImage: `linear-gradient(to right, ${a} 50%, ${b} 50%)`,
    };
  }

  if (norm.length === 3) {
    const [a, b, c] = norm;
    const thirds = ["0 33.33%", "33.33% 66.66%", "66.66% 100%"];

    // Si hay UNA imagen, la dejamos ver en su tercio (con transparente) y coloreamos los otros dos
    const idxImg = norm.findIndex((v) => isUrl(v));
    if (idxImg !== -1) {
      const colors = [a, b, c].map((v, i) =>
        i === idxImg ? "rgba(0,0,0,0)" : v
      );
      const grad = `linear-gradient(to right, ${colors[0]} ${thirds[0]}, ${colors[1]} ${thirds[1]}, ${colors[2]} ${thirds[2]})`;
      const url = norm[idxImg]!;
      return { backgroundImage: `${grad}, url(${url})` };
    }

    // 3 colores
    return {
      backgroundImage: `linear-gradient(to right, ${a} ${thirds[0]}, ${b} ${thirds[1]}, ${c} ${thirds[2]})`,
    };
  }

  // Fallback seguro
  return { backgroundColor: "#000" };
}

export const isValidEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

export const onShare = async () => {
  const url = window.location.href;

  try {
    await navigator.clipboard.writeText(url);
    console.log("URL copiada:", url);

    if (navigator.share) {
      await navigator.share({
        title: document.title,
        text: "Mira este enlace:",
        url,
      });
    } else {
      alert("URL copiada al portapapeles: " + url);
    }
  } catch (error) {
    console.error("Error al compartir:", error);
  }
};

export const isEmpty = (val: string) => {
  return val === "";
};


export const sortSizes = (sizes: string[]) => {
  const order = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL"];

  return sizes.slice().sort((a, b) => {
    const numA = parseInt(a, 10);
    const numB = parseInt(b, 10);


    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }


    const idxA = order.indexOf(a.toUpperCase());
    const idxB = order.indexOf(b.toUpperCase());
    if (idxA !== -1 && idxB !== -1) {
      return idxA - idxB;
    }

    // Caso mixto o no encontrado → fallback a orden alfabético
    return a.localeCompare(b, undefined, { numeric: true });
  });
};
